import { API_BASE_URL } from './config';
import { apiFetch } from './helpers';

interface EurojackpotData {
    jackpotAmount?: number;
}

async function fetchJackpotAmount(): Promise<number | null> {
    try {
        const response = await apiFetch(`${API_BASE_URL}/eurojackpot`);
        if (!response.ok) return null;
        const data: EurojackpotData = await response.json();
        return data.jackpotAmount ?? null;
    } catch {
        return null;
    }
}

function getNextEurojackpotDraw(): Date {
    const now = new Date();
    const helsinkiNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
    const drawHour = 21;
    const drawMinute = 0;

    const currentDay = helsinkiNow.getDay();
    const currentHour = helsinkiNow.getHours();
    const currentMinute = helsinkiNow.getMinutes();

    const drawDays = [2, 5];
    let daysUntil = Infinity;

    for (const drawDay of drawDays) {
        let diff = drawDay - currentDay;
        if (diff < 0) diff += 7;
        if (diff === 0) {
            if (currentHour > drawHour || (currentHour === drawHour && currentMinute >= drawMinute)) {
                diff = 7;
            }
        }
        if (diff < daysUntil) daysUntil = diff;
    }

    const target = new Date(helsinkiNow);
    target.setDate(target.getDate() + daysUntil);
    target.setHours(drawHour, drawMinute, 0, 0);

    const msDiff = target.getTime() - helsinkiNow.getTime();
    return new Date(now.getTime() + msDiff);
}

function formatCountdown(ms: number): { days: number; hours: number; minutes: number; seconds: number } {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds };
}

let countdownInterval: ReturnType<typeof setInterval> | null = null;

export async function renderEurojackpotCountdown(): Promise<void> {
    const container = document.getElementById('eurojackpot-countdown');
    if (!container) return;

    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }

    const nextDraw = getNextEurojackpotDraw();
    const jackpot = await fetchJackpotAmount();

    function update(): void {
        if (!container) return;
        const now = Date.now();
        const ms = nextDraw.getTime() - now;

        if (ms <= 0) {
            renderEurojackpotCountdown();
            return;
        }

        const { days, hours, minutes, seconds } = formatCountdown(ms);

        const drawDate = new Date(nextDraw);
        const dayLabel = drawDate.toLocaleDateString('fi-FI', { weekday: 'long' });
        const dateLabel = drawDate.toLocaleDateString('fi-FI', { day: 'numeric', month: 'long' });

        const jackpotHtml = jackpot ? `
            <div class="ej-jackpot">
                <span class="ej-jackpot-label">Pääpotti</span>
                <span class="ej-jackpot-amount">${(jackpot / 1_000_000).toFixed(0)} M€</span>
                <div class="ej-jackpot-meter">
                    <div class="ej-jackpot-meter-fill" style="width: ${Math.min(100, (jackpot / 120_000_000) * 100).toFixed(1)}%"></div>
                </div>
            </div>` : '';

        container.innerHTML = `
            <div class="ej-draw-label">Seuraava arvonta:<br><span class="ej-draw-day">${dayLabel} ${dateLabel}</span></div>
            <div class="ej-timer">
                <div class="ej-timer-unit">
                    <span class="ej-timer-value">${days}</span>
                    <span class="ej-timer-label">pv</span>
                </div>
                <span class="ej-timer-sep">:</span>
                <div class="ej-timer-unit">
                    <span class="ej-timer-value">${String(hours).padStart(2, '0')}</span>
                    <span class="ej-timer-label">t</span>
                </div>
                <span class="ej-timer-sep">:</span>
                <div class="ej-timer-unit">
                    <span class="ej-timer-value">${String(minutes).padStart(2, '0')}</span>
                    <span class="ej-timer-label">min</span>
                </div>
                <span class="ej-timer-sep">:</span>
                <div class="ej-timer-unit">
                    <span class="ej-timer-value">${String(seconds).padStart(2, '0')}</span>
                    <span class="ej-timer-label">s</span>
                </div>
            </div>
            ${jackpotHtml}
            <a href="https://www.veikkaus.fi/fi/eurojackpot" target="_blank" rel="noopener noreferrer" class="ej-play-link">Pelaa Veikkauksessa &rarr;</a>`;
    }

    update();
    countdownInterval = setInterval(update, 1000);
}
